import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import PageHeader from '../../components/shell/PageHeader';
import ListScreen from '../../components/list/ListScreen';
import useListState from '../../components/list/useListState';
import Drawer, { DrawerField, DrawerSection } from '../../components/overlays/Drawer';
import Button from '../../components/ui/Button';
import StatusPill from '../../components/ui/StatusPill';
import { useOverlays } from '../../components/overlays/OverlayProvider';
import {
  getServiceActionCounts,
  listServiceActions,
  updateAdminActionStatus,
} from '../../actions/agentAction';
import { formatDateTime } from '../../helpers/format';

const VIEWS = [
  { key: 'pending', label: 'Pending', attention: true },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'all', label: 'All' },
];

export default function ServiceActions() {
  const dispatch = useDispatch();
  const { confirm, toast } = useOverlays();
  const [record, setRecord] = useState(null);

  const list = useListState({
    views: VIEWS,
    fetcher: listServiceActions,
    countsFetcher: getServiceActionCounts,
    initialSort: { column: 'created_at', direction: 'desc' },
  });

  const decide = useCallback(
    async (actions, decision) => {
      const many = actions.length > 1;
      const verb = decision === 'approved' ? 'Approve' : 'Reject';
      const ok = await confirm({
        title: many ? `${verb} ${actions.length} services?` : `${verb} this service?`,
        body: many
          ? `${actions.length} service requests will be ${decision}. Approved services go live immediately.`
          : `“${actions[0].title || 'Service'}” from ${actions[0].agent_name} will be ${decision}.` +
            (decision === 'approved' ? ' It goes live immediately.' : ''),
        confirmLabel: verb,
        destructive: decision === 'rejected',
      });
      if (!ok) return;

      const results = await Promise.allSettled(
        actions.map((a) => updateAdminActionStatus(dispatch, a.id, decision))
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      toast(
        failed ? `${results.length - failed} updated, ${failed} failed` : `${results.length} ${decision}`,
        { tone: failed ? 'error' : 'success' }
      );

      setRecord(null);
      list.clearSelection();
      list.reload();
      list.reloadCounts();
    },
    [confirm, dispatch, toast, list]
  );

  const selected = useMemo(
    () => list.items.filter((r) => list.selectedIds.has(r.id)),
    [list.items, list.selectedIds]
  );

  const columns = useMemo(
    () => [
      { key: 'id', header: 'ID', width: 90, mono: true, muted: true, render: (r) => r.id.slice(0, 8) },
      { key: 'name', header: 'Name', width: 250, sortable: true, render: (r) => r.agent_name || '—' },
      {
        key: 'type',
        header: 'Type',
        width: 130,
        sortable: true,
        render: (r) => (
          <StatusPill tone={r.subtype === 'service_add' ? 'info' : 'review'}>
            {r.subtype === 'service_add' ? 'New' : 'Update'}
          </StatusPill>
        ),
      },
      { key: 'short_desc', header: 'Short description', muted: true, render: (r) => r.short_desc || '—' },
      {
        key: 'created_at',
        header: 'Created at',
        width: 170,
        muted: true,
        sortable: true,
        render: (r) => formatDateTime(r.created_at),
      },
    ],
    []
  );

  const service = record?.service || {};

  return (
    <>
      <PageHeader
        section="Partners"
        title="Service actions"
        subtitle="Approve new and edited partner services."
      />

      <ListScreen
        list={list}
        columns={columns}
        rowKey={(r) => r.id}
        noun="requests"
        searchPlaceholder="Partner or service"
        bulkActions={[
          { label: 'Approve', icon: 'bx-check', onClick: () => decide(selected, 'approved') },
          { label: 'Reject', icon: 'bx-x', onClick: () => decide(selected, 'rejected') },
        ]}
        onRowClick={setRecord}
        empty={{ icon: 'bx-package', title: 'No service requests', description: 'Nothing is waiting for review.' }}
      />

      <Drawer
        open={!!record}
        onClose={() => setRecord(null)}
        title={record?.title || 'Service request'}
        status={record?.status}
        footer={
          <>
            <Button variant="primary" onClick={() => decide([record], 'approved')}>
              Approve
            </Button>
            <Button variant="danger-text" onClick={() => decide([record], 'rejected')}>
              Reject
            </Button>
          </>
        }
      >
        <DrawerSection title="Request">
          <DrawerField label="Partner">{record?.agent_name}</DrawerField>
          <DrawerField label="Type">
            {record?.subtype === 'service_add' ? 'New service' : 'Service update'}
          </DrawerField>
          <DrawerField label="Submitted">{formatDateTime(record?.created_at)}</DrawerField>
        </DrawerSection>

        <DrawerSection title="Service definition">
          <DrawerField label="Title">{service.title}</DrawerField>
          <DrawerField label="Duration">{service.duration ? `${service.duration} min` : null}</DrawerField>
          <DrawerField label="Price" mono>
            {service.price != null ? `${service.currency === 'INR' ? '₹' : '$'}${service.price}` : null}
          </DrawerField>
          <DrawerField label="Short description">{service.short_desc}</DrawerField>
          <DrawerField label="Long description">{service.long_desc}</DrawerField>
        </DrawerSection>
      </Drawer>
    </>
  );
}
